import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  merge,
  Observable,
  switchMap,
} from 'rxjs';

import { FeatureFlagScope } from '@blaze-shared/api-contracts-domain';
import {
  accountSelectors,
  type AppState,
  appStateActions,
  type FeatureFlag,
  type FeatureFlagModel,
  featureFlagsActions,
} from '@blaze-shared/app-state-data-access';
import { FeatureFlagsApiService } from '@blaze-shared/app-state-data-access';
import {
  LocalStorageService,
  NotificationService,
  StorageKey,
} from '@blaze-shared/platform-data-access';
import { urlUtils } from '@blaze-shared/platform-util';

@Injectable()
export class FeatureFlagsEffects {
  private readonly actions$ = inject(Actions);
  private readonly featureFlagsService = inject(FeatureFlagsApiService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly notificationService = inject(NotificationService);
  private readonly store = inject<Store<AppState>>(Store);
  private readonly router = inject(Router);

  fetchFeatureFlags$ = createEffect(() =>
    merge(
      this.store.select(accountSelectors.selectAccountId),
      this.actions$.pipe(
        ofType(appStateActions.userLoggedIn),
        map(() => this.localStorageService.get<string | null>(StorageKey.AccountId)),
      ),
      // BL-9036: During login feature flags can be fetched before the redirect
      // (when we're still on the /login page). We need to react to URL changes to ensure that
      // feature flags are fetched after the redirect.
      this.router.events.pipe(
        filter((routerEvent) => routerEvent instanceof NavigationEnd),
        map(() => this.localStorageService.get<string | null>(StorageKey.AccountId)),
      ),
    ).pipe(
      filter(() => urlUtils.currentUrlRequiresAuthentication()),
      distinctUntilChanged(),
      filter((accountId): accountId is string => !!accountId),
      switchMap((accountId) => {
        return this.fetchFeatureFlags(accountId).pipe(
          catchError(() => {
            console.error('Could not fetch feature flags for accountId:', accountId);
            return EMPTY;
          }),
        );
      }),
      map((featureFlags) => featureFlagsActions.featureFlagsFetched({ featureFlags })),
    ),
  );

  updateFeatureFlag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(featureFlagsActions.featureFlagUpdate),
      concatMap(({ featureFlag }) =>
        this.featureFlagsService.insertOrUpdate(featureFlag).pipe(
          map((updatedFeatureFlag) =>
            featureFlagsActions.featureFlagUpdated({ featureFlag: updatedFeatureFlag }),
          ),
          catchError((error) => {
            console.error('Could not create feature flag:', featureFlag);
            this.notificationService.error(error?.errorMessage || 'Error creating feature flag');
            return [featureFlagsActions.featureFlagsRefresh()];
          }),
        ),
      ),
    ),
  );

  deleteFeatureFlag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(featureFlagsActions.featureFlagDelete),
      concatMap(({ featureFlag }) => {
        if (!featureFlag.id) {
          console.error('Could not delete feature flag, missing id:', featureFlag);
          this.notificationService.error('Error deleting feature flag');
          return [featureFlagsActions.featureFlagsRefresh()];
        }

        return this.featureFlagsService.delete(featureFlag.id).pipe(
          map(() => featureFlagsActions.featureFlagDeleted({ featureFlag })),
          catchError((error) => {
            console.error('Could not delete feature flag:', featureFlag);
            this.notificationService.error(error?.errorMessage || 'Error deleting feature flag');
            return [featureFlagsActions.featureFlagsRefresh()];
          }),
        );
      }),
    ),
  );

  private fetchFeatureFlags(
    accountId: string,
  ): Observable<Partial<Record<FeatureFlag, FeatureFlagModel>>> {
    return this.featureFlagsService
      .getList({
        scope: FeatureFlagScope.Account,
        scopeValue: accountId,
      })
      .pipe(
        map((featureFlags) => {
          return featureFlags.reduce(
            (acc, featureFlag) => {
              acc[featureFlag.name] = featureFlag;
              return acc;
            },
            {} as Partial<Record<FeatureFlag, FeatureFlagModel>>,
          );
        }),
      );
  }
}
